import { z } from 'zod'
import {
  DEFAULT_CALENDAR_YEAR_REPORTING_PERIOD,
  GoodsFamily,
  NON_EU_COUNTRIES_WITHOUT_POSTCODES,
  ReportingPeriodBasis
} from '@/common'

export type InstallationFormData = {
  name: string
  goodsFamily: GoodsFamily | ''
  basisForReportingPeriod?: ReportingPeriodBasis | ''
  usePartnershipAddress?: boolean
  startMonth?: number | null
  monthInterval?: number | null
  street: string
  streetNumber: string
  postCode: string
  street2: string
  city: string
  poBox: string
  country: string
  unlocode: string
  longitude: number
  latitude: number
}

export const installationDetailsSchema = z.object({
  name: z.string().min(1),
  goodsFamily: z.enum([
    GoodsFamily.ALUMINIUM,
    GoodsFamily.CEMENT,
    GoodsFamily.IRONANDSTEEL,
    GoodsFamily.ELECTRICITY,
    GoodsFamily.CHEMICALS,
    GoodsFamily.FERTILIZERS
  ])
})

const installationReportingPeriodSchema = z.object({
  basisForReportingPeriod: z.enum([
    ReportingPeriodBasis.CALENDAR_YEAR,
    ReportingPeriodBasis.FISCAL_YEAR,
    ReportingPeriodBasis.DEFINED_BY_ELIGIBLE_MONITORING_AND_VERIFICATION_SCHEME
  ]),
  startMonth: z.number().min(0).max(11),
  monthInterval: z.number().min(3).max(12)
})

export const installationAddressSchema = z
  .object({
    street: z.string().max(70).optional(),
    streetNumber: z.string().max(35).optional(),
    street2: z.string().optional(),
    postCode: z.string().optional(),
    city: z.string().min(1),
    poBox: z.string().optional(),
    country: z.string().min(1),
    unlocode: z.string().max(17).optional(),
    longitude: z.coerce.number().max(180).min(-180).default(0),
    latitude: z.coerce.number().max(90).min(-90).default(0)
  })
  .refine(
    data => {
      const doesCountryHavePostCode = !NON_EU_COUNTRIES_WITHOUT_POSTCODES.includes(data.country)

      if (doesCountryHavePostCode && !data.postCode) {
        return false
      }
      return true
    },
    {
      error: 'Post code is required for countries with post codes',
      path: ['postCode']
    }
  )

const installationFormSchema = installationDetailsSchema
  .extend(installationReportingPeriodSchema.shape)
  .extend(installationAddressSchema.shape)
  .refine(
    data => {
      const doesCountryHavePostCode = !NON_EU_COUNTRIES_WITHOUT_POSTCODES.includes(data.country)

      if (doesCountryHavePostCode && !data.postCode) {
        return false
      }
      return true
    },
    {
      error: 'Post code is required for countries with post codes',
      path: ['postCode']
    }
  )

const getReportingPeriodFields = (data: InstallationFormData) => {
  if (
    data.basisForReportingPeriod &&
    data.startMonth !== null &&
    data.startMonth !== undefined &&
    data.monthInterval !== null &&
    data.monthInterval !== undefined
  ) {
    return {
      basisForReportingPeriod: data.basisForReportingPeriod,
      startMonth: data.startMonth,
      monthInterval: data.monthInterval
    }
  }

  return DEFAULT_CALENDAR_YEAR_REPORTING_PERIOD
}

export const validateInstallationForm = (data: InstallationFormData) => {
  const reportingPeriod = getReportingPeriodFields(data)

  const validatedData = {
    name: data.name,
    goodsFamily: data.goodsFamily,
    ...reportingPeriod,
    country: data.country,
    city: data.city,
    postCode: data.postCode || undefined,
    street: data.street || undefined,
    streetNumber: data.streetNumber || undefined,
    street2: data.street2 || undefined,
    poBox: data.poBox || undefined,
    unlocode: data.unlocode || undefined,
    longitude: data.longitude || undefined,
    latitude: data.latitude || undefined
  }

  return installationFormSchema.safeParse(validatedData)
}
